import { useRef } from 'react';
import { ChevronLeft, ChevronRight, RotateCcw } from 'lucide-react';
import { CATEGORY_GROUPS } from '@wingman/shared';
import { useMapStore } from '../../store/mapStore.js';

interface CategoryChipBarProps {
  activeCategory: string | null;
  onCategoryChange: (category: string | null) => void;
}

export default function CategoryChipBar({ activeCategory, onCategoryChange }: CategoryChipBarProps) {
  const { filters, setFilters } = useMapStore();
  const scrollRef = useRef<HTMLDivElement>(null);

  const scroll = (dir: 'left' | 'right') => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollBy({ left: dir === 'left' ? -200 : 200, behavior: 'smooth' });
  };

  const hasActive = activeCategory !== null || filters.openNow || filters.minRating > 0;

  const chipStyle = (active: boolean): React.CSSProperties => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    height: 32,
    padding: '0 14px',
    border: `1px solid ${active ? 'var(--accent)' : 'var(--border)'}`,
    borderRadius: 16,
    background: active ? 'var(--accent-dim)' : 'rgba(22,27,34,0.92)',
    color: active ? 'var(--accent)' : 'var(--text-muted)',
    fontSize: 12,
    fontWeight: active ? 600 : 500,
    whiteSpace: 'nowrap',
    cursor: 'pointer',
    flexShrink: 0,
    transition: 'border-color 120ms, background 120ms, color 120ms',
    backdropFilter: 'blur(8px)',
  });

  return (
    <div
      style={{
        position: 'absolute',
        top: 'calc(var(--topbar-h) + 68px)',
        left: 12,
        right: 12,
        zIndex: 10,
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        pointerEvents: 'none',
      }}
    >
      {/* Left arrow */}
      <button
        type="button"
        onClick={() => scroll('left')}
        style={{
          ...chipStyle(false),
          padding: '0 8px',
          pointerEvents: 'all',
        }}
        aria-label="Scroll categories left"
      >
        <ChevronLeft size={14} />
      </button>

      {/* Scrollable chip row */}
      <div
        ref={scrollRef}
        style={{
          display: 'flex',
          gap: 6,
          overflowX: 'hidden',
          flex: 1,
          pointerEvents: 'all',
          scrollbarWidth: 'none',
        }}
      >
        {/* All chip */}
        <button
          type="button"
          style={chipStyle(activeCategory === null && !filters.openNow && filters.minRating === 0)}
          onClick={() => { onCategoryChange(null); setFilters({ openNow: false, minRating: 0 }); }}
        >
          All
        </button>

        {/* Open Now */}
        <button
          type="button"
          style={chipStyle(filters.openNow)}
          onClick={() => setFilters({ openNow: !filters.openNow })}
        >
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              background: filters.openNow ? 'var(--green)' : 'var(--text-dim)',
              flexShrink: 0,
            }}
          />
          Open now
        </button>

        {/* Category chips */}
        {CATEGORY_GROUPS.map((group) => (
          <button
            key={group.label}
            type="button"
            style={chipStyle(activeCategory === group.label)}
            onClick={() => onCategoryChange(activeCategory === group.label ? null : group.label)}
          >
            {group.label}
          </button>
        ))}

        {/* Rating filters */}
        <button
          type="button"
          style={chipStyle(filters.minRating === 4)}
          onClick={() => setFilters({ minRating: filters.minRating === 4 ? 0 : 4 })}
        >
          ★ 4+
        </button>
        <button
          type="button"
          style={chipStyle(filters.minRating === 3)}
          onClick={() => setFilters({ minRating: filters.minRating === 3 ? 0 : 3 })}
        >
          ★ 3+
        </button>
      </div>

      {/* Right arrow */}
      <button
        type="button"
        onClick={() => scroll('right')}
        style={{
          ...chipStyle(false),
          padding: '0 8px',
          pointerEvents: 'all',
        }}
        aria-label="Scroll categories right"
      >
        <ChevronRight size={14} />
      </button>

      {/* Reset */}
      {hasActive && (
        <button
          type="button"
          onClick={() => { onCategoryChange(null); setFilters({ openNow: false, minRating: 0 }); }}
          style={{
            ...chipStyle(false),
            padding: '0 10px',
            pointerEvents: 'all',
          }}
          aria-label="Clear filters"
          title="Clear all filters"
        >
          <RotateCcw size={12} />
        </button>
      )}
    </div>
  );
}
